import { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { VenueComplex, CreateComplexData } from '../../types/venue';

interface UseComplexFormReturn {
  formData: CreateComplexData;
  errors: Record<string, string>;
  loading: boolean;
  updateField: <K extends keyof CreateComplexData>(
    field: K,
    value: CreateComplexData[K]
  ) => void;
  submitForm: () => Promise<{
    success: boolean;
    complex?: VenueComplex;
    error?: string;
  }>;
}

export const useComplexForm = (
  initialData?: VenueComplex
): UseComplexFormReturn => {
  const [formData, setFormData] = useState<CreateComplexData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    address: initialData?.address || '',
    mapsUrl: initialData?.mapsUrl || '',
    city: initialData?.city || '',
    province: initialData?.province || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
    website: initialData?.website || '',
    hasParking: initialData?.hasParking || false,
    hasBathrooms: initialData?.hasBathrooms ?? true,
    hasChangingRooms: initialData?.hasChangingRooms || false,
    hasBuffet: initialData?.hasBuffet || false,
    hasProShop: initialData?.hasProShop || false,
    amenities: initialData?.amenities || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        address: initialData.address || '',
        mapsUrl: initialData.mapsUrl || '',
        city: initialData.city || '',
        province: initialData.province || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        website: initialData.website || '',
        hasParking: initialData.hasParking || false,
        hasBathrooms: initialData.hasBathrooms ?? true,
        hasChangingRooms: initialData.hasChangingRooms || false,
        hasBuffet: initialData.hasBuffet || false,
        hasProShop: initialData.hasProShop || false,
        amenities: initialData.amenities || [],
      });
    }
  }, [initialData]);

  const updateField = <K extends keyof CreateComplexData>(
    field: K,
    value: CreateComplexData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'La dirección es requerida';
    }
    if (!formData.mapsUrl.trim()) {
      newErrors.mapsUrl = 'El link de Google Maps es requerido';
    } else if (
      !formData.mapsUrl.includes('google.com/maps') &&
      !formData.mapsUrl.includes('maps.app.goo.gl') &&
      !formData.mapsUrl.includes('share.google') &&
      !formData.mapsUrl.includes('goo.gl/maps')
    ) {
      newErrors.mapsUrl = 'Debe ser un link válido de Google Maps';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'La ciudad es requerida';
    }
    if (!formData.province.trim()) {
      newErrors.province = 'La provincia es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    if (!validateForm()) {
      return { success: false, error: 'Por favor completá todos los campos requeridos' };
    }

    try {
      setLoading(true);

      const method = initialData ? 'PUT' : 'POST';
      const endpoint = initialData
        ? `/complexes/${initialData.id}`
        : '/complexes';

      const response = await apiRequest<{ complex: VenueComplex }>(endpoint, {
        method,
        body: formData,
      });

      if (response.data?.complex) {
        return { success: true, complex: response.data.complex };
      } else {
        return { success: false, error: response.error || 'Error al guardar el complejo' };
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error submitting complex:', error);
      return { success: false, error: error.message || 'Error al guardar el complejo' };
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    errors,
    loading,
    updateField,
    submitForm,
  };
};
